// Chart-expand overlay — click any expandable ChartCard to enlarge it.
// Recast of the estimate-peek slide-over pattern, centered. Animates transform
// + opacity only (never width/height) so recharts measures layout once and the
// old ResponsiveContainer resize loop can't be re-triggered.

import { useCallback, useEffect, useRef, useState } from "react"
import { AnimatePresence, motion } from "motion/react"
import { XClose } from "@untitledui/icons"
import { ExpandContext, type ExpandPayload } from "./room-chrome"
import { HEADLINE } from "./room-model"

export function ExpandProvider({ children }: { children: React.ReactNode }) {
  const [payload, setPayload] = useState<ExpandPayload | null>(null)
  const openerRef = useRef<HTMLElement | null>(null)

  const openExpand = useCallback((p: ExpandPayload) => {
    openerRef.current = (document.activeElement as HTMLElement) ?? null
    setPayload(p)
  }, [])
  const close = useCallback(() => {
    setPayload(null)
    openerRef.current?.focus?.()
  }, [])

  useEffect(() => {
    if (!payload) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [payload, close])

  return (
    <ExpandContext.Provider value={openExpand}>
      {children}
      <AnimatePresence>
        {payload && (
          <>
            <motion.button
              aria-label="Close enlarged chart"
              className="fixed inset-0 z-[60] bg-zinc-900/30 backdrop-blur-sm"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={close}
            />
            <div className="fixed inset-0 z-[61] grid place-items-center p-4 pointer-events-none">
              <motion.div
                role="dialog" aria-modal="true" aria-label={payload.title}
                className="pointer-events-auto w-[min(1100px,92vw)] max-h-[88vh] overflow-auto rounded-3xl bg-white p-8 shadow-2xl min-w-0"
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
                transition={{ type: "tween", duration: 0.18, ease: "easeOut" }}
              >
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold text-zinc-900" style={HEADLINE}>{payload.title}</h2>
                    {payload.sub && <p className="text-sm text-[#8B897E] mt-1">{payload.sub}</p>}
                  </div>
                  <button onClick={close} aria-label="Close" autoFocus
                    className="shrink-0 p-2 rounded-lg text-[#8B897E] hover:text-zinc-700 hover:bg-[#F1F0EB] transition-colors">
                    <XClose className="size-5" aria-hidden />
                  </button>
                </div>
                {payload.render()}
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </ExpandContext.Provider>
  )
}
